import {
  BookingProductType,
  BookingState,
  isBookingProductTypePackage,
  isBookingProductTypeTariff,
  ProductTariffUuid
} from '@products/models/booking.model';
import { cloneDeep } from 'lodash';

/**
 * Set booking product holder uuid
 * @description Set booking product holder uuid
 * @param state
 * @param props
 * @returns State
 */
export const setBookingProductTariffUuid = (state: BookingState, props: { productTariffUuid: ProductTariffUuid }) => {
  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);

  const {
    tariffUuid,
    productType: holderProductType,
    ticketTypeId: holderTicketTypeId,
    ticketPersonId: holderTicketPersonId,
    packageIndex: holderPackageIndex,
    packageNumber: holderPackageNumber
  }: ProductTariffUuid = cloneDeep(props.productTariffUuid);

  const holderStatus = {
    isHolderUuidAssigned: false,
    isExistingHolderUuidRemoved: false
  };

  cloneStateProductsList.forEach((bookedProductType) => {
    if (holderStatus.isHolderUuidAssigned && holderStatus.isExistingHolderUuidRemoved) {
      return;
    }

    if (isBookingProductTypeTariff(bookedProductType)) {
      const { tariffs, productType } = bookedProductType;

      tariffs.forEach((bookedTariff) => {
        if (!holderStatus.isExistingHolderUuidRemoved && bookedTariff.tariffUuids.includes(tariffUuid)) {
          const indexOfTariffUuid = bookedTariff.tariffUuids.indexOf(tariffUuid);
          bookedTariff.tariffUuids.splice(indexOfTariffUuid, 1);
          holderStatus.isExistingHolderUuidRemoved = true;
        } else if (
          productType === holderProductType &&
          bookedTariff.ticketTypeId === holderTicketTypeId &&
          bookedTariff.ticketPersonId === holderTicketPersonId
        ) {
          bookedTariff.tariffUuids.push(tariffUuid);
          holderStatus.isHolderUuidAssigned = true;
        }
      });
    } else if (isBookingProductTypePackage(bookedProductType)) {
      const { productType, packageNumber, packageIndex } = bookedProductType;
      const isTicketHolderAssignmentPackage =
        productType === holderProductType &&
        packageNumber === holderPackageNumber &&
        packageIndex === holderPackageIndex;

      bookedProductType.productTypes.forEach((bookedPackageProductType) => {
        if (isBookingProductTypeTariff(bookedPackageProductType)) {
          bookedPackageProductType.tariffs.forEach((bookedPackageTariff) => {
            if (!holderStatus.isExistingHolderUuidRemoved && bookedPackageTariff.tariffUuids.includes(tariffUuid)) {
              const indexOfTicketTariffUuid = bookedPackageTariff.tariffUuids.indexOf(tariffUuid);
              bookedPackageTariff.tariffUuids.splice(indexOfTicketTariffUuid, 1);
              holderStatus.isExistingHolderUuidRemoved = true;
            } else if (
              isTicketHolderAssignmentPackage &&
              bookedPackageTariff.ticketTypeId === holderTicketTypeId &&
              bookedPackageTariff.ticketPersonId === holderTicketPersonId
            ) {
              bookedPackageTariff.tariffUuids.push(tariffUuid);
              holderStatus.isHolderUuidAssigned = true;
            }
          });
        }
      });
    }
  });

  if (holderStatus.isHolderUuidAssigned) {
    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};

/**
 * Remove booking product holder uuid
 * @description Remove booking product holder uuid
 * @param state
 * @param props
 * @returns State
 */
export const removeBookingProductTariffUuid = (
  state: BookingState,
  props: { productTariffUuid: ProductTariffUuid }
) => {
  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);

  const {
    tariffUuid,
    productType: holderProductType,
    ticketTypeId: holderTicketTypeId,
    ticketPersonId: holderTicketPersonId,
    packageIndex: holderPackageIndex,
    packageNumber: holderPackageNumber
  }: ProductTariffUuid = cloneDeep(props.productTariffUuid);

  const ticketHolderRemoved = cloneStateProductsList.find((bookedProductType) => {
    if (isBookingProductTypeTariff(bookedProductType)) {
      const { tariffs, productType } = bookedProductType;

      return !!tariffs.find((bookedTariff) => {
        if (
          productType === holderProductType &&
          bookedTariff.tariffUuids.includes(tariffUuid) &&
          bookedTariff.ticketTypeId === holderTicketTypeId &&
          bookedTariff.ticketPersonId === holderTicketPersonId
        ) {
          const indexOfTariffUuid = bookedTariff.tariffUuids.indexOf(tariffUuid);
          bookedTariff.tariffUuids.splice(indexOfTariffUuid, 1);

          return !!bookedTariff;
        }
      });
    } else if (isBookingProductTypePackage(bookedProductType)) {
      const { productType, packageNumber, packageIndex } = bookedProductType;

      if (
        productType === holderProductType &&
        packageNumber === holderPackageNumber &&
        packageIndex === holderPackageIndex
      ) {
        return !!bookedProductType.productTypes.find((bookedPackageProductType) => {
          if (isBookingProductTypeTariff(bookedPackageProductType)) {
            return !!bookedPackageProductType.tariffs.find((bookedPackageTariff) => {
              if (
                bookedPackageTariff.tariffUuids.includes(tariffUuid) &&
                bookedPackageTariff.ticketTypeId === holderTicketTypeId &&
                bookedPackageTariff.ticketPersonId === holderTicketPersonId
              ) {
                const indexOfTariffUuid = bookedPackageTariff.tariffUuids.indexOf(tariffUuid);
                bookedPackageTariff.tariffUuids.splice(indexOfTariffUuid, 1);

                return !!bookedPackageTariff;
              }
            });
          }
        });
      }
    }
  });

  if (ticketHolderRemoved) {
    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};
